/**
 * Supabase Gamification Repository
 *
 * Implements IGamificationRepository for Supabase storage.
 * Handles XP, achievements, streaks, and leaderboard operations.
 *
 * @module repositories/supabase/SupabaseGamificationRepository
 */
import { getSupabaseClient } from '../../services/auth/supabase';
import type {
  IGamificationRepository,
  Unsubscribe,
  AwardXPOptions,
} from '../interfaces/IGamificationRepository';
import type {
  UserGamificationProfile,
  UserGamificationProfileUpdate,
  XPEvent,
  XPEventType,
  UserAchievement,
  StreakData,
  Leaderboard,
  LeaderboardEntry,
  LeaderboardPeriod,
  LeaderboardType,
  StreakMilestone,
} from '../../types/gamification';
import {
  XP_AWARDS,
  calculateLevel,
  calculateStreakMultiplier,
} from '../../types/gamification';
import type { EducationCategory } from '../../types/education';
import type { Database, Json } from '../../types/database.types';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { clientLogger } from '@/lib/clientLogger';

type GamificationRow = Database['public']['Tables']['user_gamification']['Row'];
type GamificationInsert = Database['public']['Tables']['user_gamification']['Insert'];
type GamificationDbUpdate = Database['public']['Tables']['user_gamification']['Update'];
type AchievementRow = Database['public']['Tables']['user_achievements']['Row'];
type AchievementInsert = Database['public']['Tables']['user_achievements']['Insert'];
type XPEventRow = Database['public']['Tables']['xp_events']['Row'];
type XPEventInsert = Database['public']['Tables']['xp_events']['Insert'];

// =============================================================================
// Default Values
// =============================================================================

const DEFAULT_STREAK_DATA: StreakData = {
  current: 0,
  longest: 0,
  lastActivityDate: '',
  todayCompleted: false,
  isFrozen: false,
  freezesRemaining: 0,
  milestonesAchieved: [],
};

const DEFAULT_XP_BY_CATEGORY: Record<EducationCategory, number> = {
  'foundations': 0,
  'risk-process': 0,
  'structure-trend': 0,
  'precision-tools': 0,
  'professional-edge': 0,
  'risk-management': 0,
  'technical-analysis': 0,
  'fundamental-analysis': 0,
  'trading-psychology': 0,
  'market-structure': 0,
  'order-flow': 0,
  'strategy': 0,
};

// =============================================================================
// Row Conversion Helpers
// =============================================================================

/**
 * Convert database row to UserGamificationProfile domain object.
 */
function rowToProfile(row: GamificationRow): UserGamificationProfile {
  const totalXP = row.total_xp ?? 0;
  const levelInfo = calculateLevel(totalXP);
  const badges = (row.badges as unknown as Record<string, unknown>) ?? {};

  return {
    userId: row.user_id,
    totalXP,
    level: levelInfo.level,
    xpInCurrentLevel: levelInfo.xpInLevel,
    xpToNextLevel: levelInfo.xpToNext,
    xpByCategory: (badges.xpByCategory as Record<EducationCategory, number>) ?? { ...DEFAULT_XP_BY_CATEGORY },
    streak: {
      current: row.streak_days ?? 0,
      longest: row.longest_streak ?? 0,
      lastActivityDate: row.last_activity_date ?? '',
      todayCompleted: isToday(row.last_activity_date),
      isFrozen: false,
      freezesRemaining: 0,
      milestonesAchieved: (badges.streakMilestones as StreakMilestone[]) ?? [],
    },
    lessonsCompleted: (badges.lessonsCompleted as number) ?? 0,
    quizzesPassed: (badges.quizzesPassed as number) ?? 0,
    perfectQuizzes: (badges.perfectQuizzes as number) ?? 0,
    dailyChallengesCompleted: (badges.dailyChallengesCompleted as number) ?? 0,
    reviewsCompleted: (badges.reviewsCompleted as number) ?? 0,
    totalTimeSpent: (badges.totalTimeSpent as number) ?? 0,
    createdAt: row.created_at,
    lastActivityAt: row.updated_at,
    lastXPEarnedAt: row.last_activity_date ?? undefined,
  };
}

/**
 * Convert XPEvent row to domain object.
 */
function rowToXPEvent(row: XPEventRow): XPEvent {
  const metadata = (row.metadata as Record<string, unknown>) ?? {};
  return {
    id: row.id,
    userId: row.user_id,
    type: row.event_type as XPEventType,
    baseXP: (metadata.baseXP as number) ?? row.xp_earned,
    multiplier: (metadata.multiplier as number) ?? 1,
    totalXP: row.xp_earned,
    contentId: row.source ?? undefined,
    contentType: metadata.contentType as XPEvent['contentType'],
    category: metadata.category as EducationCategory,
    timestamp: row.created_at,
    metadata,
  };
}

/**
 * Convert UserAchievement row to domain object.
 */
function rowToUserAchievement(row: AchievementRow): UserAchievement {
  return {
    achievementId: row.achievement_id,
    userId: row.user_id,
    progress: 1, // Unlocked achievements have 100% progress
    isUnlocked: true,
    unlockedAt: row.unlocked_at,
    hasSeenNotification: true, // Default to seen for loaded achievements
  };
}

/**
 * Check if a date string is today.
 */
function isToday(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const today = new Date().toISOString().split('T')[0];
  return dateStr.startsWith(today);
}

/**
 * Get today's date as ISO string (date only).
 */
function getTodayDateStr(): string {
  return new Date().toISOString().split('T')[0];
}

// =============================================================================
// Repository Implementation
// =============================================================================

export class SupabaseGamificationRepository implements IGamificationRepository {
  // ===========================================================================
  // Profile Operations
  // ===========================================================================

  async getUserGamificationProfile(userId: string): Promise<UserGamificationProfile | null> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('user_gamification')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to fetch gamification profile: ${error.message}`);
    }
    if (!data) return null;

    return rowToProfile(data);
  }

  async createGamificationProfile(userId: string): Promise<UserGamificationProfile> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const insertData: GamificationInsert = {
      user_id: userId,
      total_xp: 0,
      level: 1,
      streak_days: 0,
      longest_streak: 0,
      badges: {
        xpByCategory: DEFAULT_XP_BY_CATEGORY,
        lessonsCompleted: 0,
        quizzesPassed: 0,
        perfectQuizzes: 0,
        dailyChallengesCompleted: 0,
        reviewsCompleted: 0,
        totalTimeSpent: 0,
        streakMilestones: [],
      } as unknown as Json,
    };

    const { data, error } = await supabase
      .from('user_gamification')
      .insert(insertData)
      .select()
      .single();

    if (error) throw new Error(`Failed to create gamification profile: ${error.message}`);
    return rowToProfile(data);
  }

  async getOrCreateGamificationProfile(userId: string): Promise<UserGamificationProfile> {
    const existing = await this.getUserGamificationProfile(userId);
    if (existing) return existing;
    return this.createGamificationProfile(userId);
  }

  async updateGamificationProfile(
    userId: string,
    updates: UserGamificationProfileUpdate
  ): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    // Get current profile to merge badges
    const current = await this.getOrCreateGamificationProfile(userId);

    const updateData: GamificationDbUpdate = {
      updated_at: new Date().toISOString(),
    };

    if (updates.totalXP !== undefined) {
      updateData.total_xp = updates.totalXP;
      updateData.level = calculateLevel(updates.totalXP).level;
    }

    if (updates.streak !== undefined) {
      updateData.streak_days = updates.streak.current;
      updateData.longest_streak = Math.max(
        updates.streak.longest,
        current.streak.longest
      );
      updateData.last_activity_date = updates.streak.lastActivityDate;
    }

    // Merge badge data
    const badges: Record<string, unknown> = {
      xpByCategory: updates.xpByCategory ?? current.xpByCategory,
      lessonsCompleted: updates.lessonsCompleted ?? current.lessonsCompleted,
      quizzesPassed: updates.quizzesPassed ?? current.quizzesPassed,
      perfectQuizzes: updates.perfectQuizzes ?? current.perfectQuizzes,
      dailyChallengesCompleted: updates.dailyChallengesCompleted ?? current.dailyChallengesCompleted,
      reviewsCompleted: updates.reviewsCompleted ?? current.reviewsCompleted,
      totalTimeSpent: updates.totalTimeSpent ?? current.totalTimeSpent,
      streakMilestones: updates.streak?.milestonesAchieved ?? current.streak.milestonesAchieved,
    };
    updateData.badges = badges as unknown as Json;

    const { error } = await supabase
      .from('user_gamification')
      .update(updateData)
      .eq('user_id', userId);

    if (error) throw new Error(`Failed to update gamification profile: ${error.message}`);
  }

  // ===========================================================================
  // XP Operations
  // ===========================================================================

  async awardXP(userId: string, eventType: XPEventType, options?: AwardXPOptions): Promise<XPEvent> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    // Get XP config for this event type
    const xpConfig = XP_AWARDS[eventType];
    const baseXP = xpConfig?.baseXP ?? 0;

    // Get current profile for streak multiplier
    const profile = await this.getOrCreateGamificationProfile(userId);
    const streakMultiplier = calculateStreakMultiplier(profile.streak.current);
    const bonusMultiplier = options?.bonusMultiplier ?? 1;
    const totalMultiplier = streakMultiplier * bonusMultiplier;
    const totalXP = Math.round(baseXP * totalMultiplier);

    // Create XP event record
    const xpEventInsert: XPEventInsert = {
      user_id: userId,
      event_type: eventType,
      xp_earned: totalXP,
      source: options?.contentId ?? null,
      metadata: {
        baseXP,
        multiplier: totalMultiplier,
        contentType: options?.contentType,
        category: options?.category,
        ...options?.metadata,
      } as unknown as Json,
    };

    const { data: xpEventData, error: xpError } = await supabase
      .from('xp_events')
      .insert(xpEventInsert)
      .select()
      .single();

    if (xpError) throw new Error(`Failed to create XP event: ${xpError.message}`);

    // Update profile with new XP total
    const newTotalXP = profile.totalXP + totalXP;
    const xpByCategory = { ...profile.xpByCategory };
    if (options?.category) {
      xpByCategory[options.category] = (xpByCategory[options.category] ?? 0) + totalXP;
    }

    await this.updateGamificationProfile(userId, {
      totalXP: newTotalXP,
      xpByCategory,
      lastXPEarnedAt: new Date().toISOString(),
    });

    return rowToXPEvent(xpEventData);
  }

  async getXPHistory(
    userId: string,
    options?: { limit?: number; startAfter?: string }
  ): Promise<XPEvent[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    let query = supabase
      .from('xp_events')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    if (options?.startAfter) {
      query = query.lt('created_at', options.startAfter);
    }

    const { data, error } = await query;

    if (error) throw new Error(`Failed to fetch XP history: ${error.message}`);
    return data.map(rowToXPEvent);
  }

  // ===========================================================================
  // Streak Operations
  // ===========================================================================

  async updateStreak(userId: string): Promise<StreakData> {
    const profile = await this.getOrCreateGamificationProfile(userId);
    const today = getTodayDateStr();
    const lastActivity = profile.streak.lastActivityDate;

    let newStreak = profile.streak.current;
    let newLongest = profile.streak.longest;

    if (!lastActivity) {
      // First activity ever
      newStreak = 1;
    } else if (lastActivity === today) {
      // Already logged today, no change
      return profile.streak;
    } else {
      // Check if last activity was yesterday
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];

      if (lastActivity === yesterdayStr) {
        // Continue streak
        newStreak = profile.streak.current + 1;
      } else {
        // Streak broken, reset to 1
        newStreak = 1;
      }
    }

    // Update longest streak if needed
    newLongest = Math.max(newLongest, newStreak);

    // Check for new milestones
    const milestones: StreakMilestone[] = [7, 14, 30, 60, 100, 365];
    const achievedMilestones = [...profile.streak.milestonesAchieved];
    for (const milestone of milestones) {
      if (newStreak >= milestone && !achievedMilestones.includes(milestone)) {
        achievedMilestones.push(milestone);
      }
    }

    const updatedStreak: StreakData = {
      current: newStreak,
      longest: newLongest,
      lastActivityDate: today,
      todayCompleted: true,
      isFrozen: profile.streak.isFrozen,
      freezesRemaining: profile.streak.freezesRemaining,
      milestonesAchieved: achievedMilestones,
    };

    await this.updateGamificationProfile(userId, { streak: updatedStreak });
    return updatedStreak;
  }

  async useStreakFreeze(userId: string): Promise<boolean> {
    const profile = await this.getOrCreateGamificationProfile(userId);

    if (profile.streak.freezesRemaining <= 0) {
      return false;
    }

    const updatedStreak: StreakData = {
      ...profile.streak,
      freezesRemaining: profile.streak.freezesRemaining - 1,
      isFrozen: true,
      freezeExpiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
    };

    await this.updateGamificationProfile(userId, { streak: updatedStreak });
    return true;
  }

  // ===========================================================================
  // Achievement Operations
  // ===========================================================================

  async getUserAchievements(userId: string): Promise<UserAchievement[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('user_achievements')
      .select('*')
      .eq('user_id', userId)
      .order('unlocked_at', { ascending: false });

    if (error) throw new Error(`Failed to fetch achievements: ${error.message}`);
    return data.map(rowToUserAchievement);
  }

  async updateAchievementProgress(
    userId: string,
    achievementId: string,
    progressIncrement: number = 1
  ): Promise<UserAchievement> {
    // Note: The current DB schema only tracks unlocked achievements
    // For progress tracking, we'd need to store in-progress achievements
    // For now, return a synthetic progress object
    return {
      achievementId,
      userId,
      progress: progressIncrement,
      isUnlocked: false,
      hasSeenNotification: false,
    };
  }

  async unlockAchievement(userId: string, achievementId: string, xpReward: number): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const insertData: AchievementInsert = {
      user_id: userId,
      achievement_id: achievementId,
      unlocked_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('user_achievements')
      .upsert(insertData, { onConflict: 'user_id,achievement_id' });

    if (error) throw new Error(`Failed to unlock achievement: ${error.message}`);

    // Award XP for achievement
    if (xpReward > 0) {
      await this.awardXP(userId, 'achievement_unlock', {
        contentId: achievementId,
        contentType: 'review',
        bonusMultiplier: xpReward / (XP_AWARDS.achievement_unlock?.baseXP || 1),
      });
    }
  }

  async markAchievementSeen(userId: string, achievementId: string): Promise<void> {
    // The current DB schema doesn't track notification status
    // This would require a schema update or storing in the badges JSON
    clientLogger.info(`[SupabaseGamificationRepository] markAchievementSeen: ${userId}, ${achievementId}`);
  }

  // ===========================================================================
  // Leaderboard Operations
  // ===========================================================================

  async getLeaderboard(
    type: LeaderboardType,
    period: LeaderboardPeriod,
    topN: number = 10
  ): Promise<Leaderboard> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    // Determine the column to sort by
    let orderColumn = 'total_xp';
    if (type === 'streak') orderColumn = 'streak_days';

    // For period filtering, we'd need to track XP per period
    // For now, we use all-time data
    const { data, error } = await supabase
      .from('user_gamification')
      .select('user_id, total_xp, level, streak_days')
      .order(orderColumn, { ascending: false })
      .limit(topN);

    if (error) throw new Error(`Failed to fetch leaderboard: ${error.message}`);

    // Get user display names
    const userIds = data.map((row) => row.user_id);
    const { data: users } = await supabase
      .from('users')
      .select('id, display_name, avatar_url')
      .in('id', userIds);

    const userMap = new Map(users?.map((u) => [u.id, u]) ?? []);

    const entries: LeaderboardEntry[] = data.map((row, index) => {
      const user = userMap.get(row.user_id);
      return {
        userId: row.user_id,
        displayName: user?.display_name ?? 'Anonymous',
        avatarUrl: user?.avatar_url ?? undefined,
        rank: index + 1,
        score: type === 'streak' ? (row.streak_days ?? 0) : (row.total_xp ?? 0),
        level: row.level ?? 1,
        streak: row.streak_days ?? 0,
      };
    });

    const now = new Date();
    const periodStart = new Date();
    const periodEnd = new Date();

    switch (period) {
      case 'daily':
        periodStart.setHours(0, 0, 0, 0);
        periodEnd.setHours(23, 59, 59, 999);
        break;
      case 'weekly':
        periodStart.setDate(now.getDate() - now.getDay());
        periodStart.setHours(0, 0, 0, 0);
        periodEnd.setDate(periodStart.getDate() + 6);
        periodEnd.setHours(23, 59, 59, 999);
        break;
      case 'monthly':
        periodStart.setDate(1);
        periodStart.setHours(0, 0, 0, 0);
        periodEnd.setMonth(periodEnd.getMonth() + 1);
        periodEnd.setDate(0);
        periodEnd.setHours(23, 59, 59, 999);
        break;
      case 'allTime':
      default:
        periodStart.setFullYear(2020);
        periodEnd.setFullYear(2100);
        break;
    }

    return {
      type,
      period,
      entries,
      totalParticipants: entries.length,
      lastUpdatedAt: now.toISOString(),
      periodStartAt: periodStart.toISOString(),
      periodEndAt: periodEnd.toISOString(),
    };
  }

  async getUserRank(userId: string, type: LeaderboardType): Promise<LeaderboardEntry | null> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    // Get user's data
    const { data: userData, error: userError } = await supabase
      .from('user_gamification')
      .select('user_id, total_xp, level, streak_days')
      .eq('user_id', userId)
      .maybeSingle();

    if (userError) {
      throw new Error(`Failed to fetch user rank: ${userError.message}`);
    }
    if (!userData) return null;

    // Count users with higher score
    const scoreColumn = type === 'streak' ? 'streak_days' : 'total_xp';
    const userScore = type === 'streak' ? (userData.streak_days ?? 0) : (userData.total_xp ?? 0);

    const { count, error: countError } = await supabase
      .from('user_gamification')
      .select('*', { count: 'exact', head: true })
      .gt(scoreColumn, userScore);

    if (countError) throw new Error(`Failed to calculate rank: ${countError.message}`);

    // Get display name
    const { data: userProfile } = await supabase
      .from('users')
      .select('display_name, avatar_url')
      .eq('id', userId)
      .maybeSingle();

    return {
      userId,
      displayName: userProfile?.display_name ?? 'Anonymous',
      avatarUrl: userProfile?.avatar_url ?? undefined,
      rank: (count ?? 0) + 1,
      score: userScore,
      level: userData.level ?? 1,
      streak: userData.streak_days ?? 0,
    };
  }

  // ===========================================================================
  // Stat Increment Helpers
  // ===========================================================================

  async incrementStat(
    userId: string,
    stat: 'lessonsCompleted' | 'quizzesPassed' | 'perfectQuizzes' | 'reviewsCompleted',
    amount: number = 1
  ): Promise<void> {
    const profile = await this.getOrCreateGamificationProfile(userId);

    const updates: Partial<UserGamificationProfileUpdate> = {};
    updates[stat] = (profile[stat] ?? 0) + amount;

    await this.updateGamificationProfile(userId, updates);
  }

  async addTimeSpent(userId: string, minutes: number): Promise<void> {
    const profile = await this.getOrCreateGamificationProfile(userId);

    await this.updateGamificationProfile(userId, {
      totalTimeSpent: (profile.totalTimeSpent ?? 0) + minutes,
    });
  }

  // ===========================================================================
  // Real-time Subscriptions
  // ===========================================================================

  subscribeToProfile(
    userId: string,
    callback: (profile: UserGamificationProfile | null) => void
  ): Unsubscribe {
    const supabase = getSupabaseClient();
    if (!supabase) {
      clientLogger.warn('[SupabaseGamificationRepository] Cannot subscribe: Supabase not configured');
      callback(null);
      return () => {};
    }

    let channel: RealtimeChannel | null = null;

    // Initial fetch
    this.getUserGamificationProfile(userId)
      .then(callback)
      .catch((err) => {
        clientLogger.error('[SupabaseGamificationRepository] Initial fetch failed:', err);
        callback(null);
      });

    // Set up real-time subscription
    channel = supabase
      .channel(`user_gamification:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_gamification',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // Re-fetch profile on any change
          this.getUserGamificationProfile(userId)
            .then(callback)
            .catch((err) => clientLogger.error('[SupabaseGamificationRepository] Refetch failed:', err));
        }
      )
      .subscribe();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }
}
